<template>
    <div v-if="widgetReady">
        <div v-if="existingModifiedCount && hasPatients" class="alert alert-warning">
            Editing discounts on plans in use will cause all changes made here to begin in the future
            and will lock them from being modified until then.
        </div>
        <div class="row margin-bottom-30" style="text-align: center; font-weight: bold;">
            <div class="col-lg-9 word-break-all pb-2">
                <div class="row">
                    <div class="col-lg-3">
                        <span>Discount Name</span>
                    </div>
                    <div class="col-lg-6">
                        <span>Discount Range</span>
                    </div>
                    <div class="col-lg-3">
                        <span>Per Person Discount %</span>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="discounts.length>0" class="margin-bottom-3" style="text-align: center;">
            <div v-for="(d, index) in discounts">
                <div v-if="d.editMode == false" class="row margin-bottom-30 discount-row">
                    <div class="col-lg-9 word-break-all pb-2">
                        <div class="row">
                            <div class="col-lg-3">
                                {{ d.name }}
                            </div>
                            <div class="col-lg-6 slider-widget">
                                <span id="slider-left" class="hidden">low value</span>
                                <span id="slider-right" class="hidden">high value</span>
                                <b><i class="fa fa-user"></i> 1 </b>
                                <input type="text" :ref="d.sliderKey"/>
                                <b><i class="fa fa-users"></i> 15 </b>
                            </div>
                            <div class="col-lg-3">
                                {{ d.discountBundleDetails.individualDiscount }}%
                            </div>
                        </div>
                    </div>
                    <div class="col-lg-3 pb-2" style="text-align: left;">
                        <div class="future-settings-container" v-if="Boolean(d.futureSettings)">
                            <div class="pending-link">
                                <div class="future-discount">
                                    <future-discount :future-settings="viewFutureDiscount(d)"></future-discount>
                                </div>
                                <span class="label label-warning">View Pending Change</span>
                            </div>
                        </div>
                        <template v-else>
                            <button v-if="!readOnly" :disabled="isRowBeingEdited" class="btn-sm btn-success edit-button" @click.prevent="()=>{d.editMode = true}">
                                <span style="color: white !important;">EDIT</span>
                            </button>
                            <button v-if="!d.exist" :disabled="isRowBeingEdited" class="btn-sm btn-red outline-btn" @click.prevent="()=>{deleteDiscount(index, true)}">
                                <span>DELETE</span>
                            </button>
                        </template>
                    </div>
                </div>
                <div v-if="d.editMode" class="row" style="text-align: left;">
                    <div class="col-lg-9">
                        <add-discount-form :data-key="index" ref="discountEditForm" v-model="discounts[index]"></add-discount-form>
                    </div>
                    <div class="col-lg-3">
                        <ac-button class="btn-sm btn-success wl-btn-success outline-btn" :action="()=>{saveEdit(index)}">
                            DONE
                        </ac-button>
                        <ac-button class="btn-sm btn-red outline-btn" :action="()=>{cancelEdit(index)}">
                            CANCEL
                        </ac-button>
                    </div>
                </div>
            </div>
        </div>
        <template v-if="!readOnly">
            <div v-if="addDiscountForm" class="row">
                <div class="col-lg-9">
                    <add-discount-form ref="discountForm" v-model="discountToBeAdded"></add-discount-form>
                </div>
                <div class="col-lg-3">
                    <div>
                        <ac-button class="btn-sm btn-success wl-btn-success" :action="addDiscountToList">
                            ADD
                        </ac-button>
                        <ac-button class="btn-sm btn-red" :action="deleteDiscount">
                            DELETE
                        </ac-button>
                    </div>
                </div>
            </div>
        </template>
        <div v-if="futureOnlyDiscounts.length > 0">
            <div v-for="future in futureOnlyDiscounts">
                <div class="alert alert-info">
                    New Discount: {{ future.name }} begins on {{ future.effectiveDate | shortDate }}
                    with a range of {{ JSON.parse(future.details).MinRange }}-{{ JSON.parse(future.details).MaxRange }}
                    at {{ JSON.parse(future.details).IndividualDiscount }}%
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import {api} from "../../../../global/framework/api.js";
    import AddDiscountForm from "./AddDiscountForm.vue";
    import FutureDiscount from "./FutureDiscount.vue";
    import _ from 'lodash';

    export default {
        name: "AddDiscounts",
        components: {AddDiscountForm, FutureDiscount},
        props: {
            planId: {type: [Number], required: false, default: 0},
            discountsPassed: {type: [Array], required: false},
            readOnly: {type: [Boolean], required: false, default: false}
        },
        data() {
            return {
                discounts: [],
                discountTemplate: {},
                discountToBeAdded: {},
                widgetReady: false,
                currentDiscounts: [],
                futureOnlyDiscounts: [],
                hasPatients: false,
                slider: null,
                template: {},
                futureSettings: {},
                addDiscountForm: false,
            }
        },
        computed: {
            isRowBeingEdited() {
                if (this.discounts.some(d => d.editMode == true)) {
                    return true;
                }
                return false;
            },
            existingModifiedCount() {
                return this.discounts.filter(x => x.modified && x.discountId).length;
            }
        },
        methods: {
            async addDiscount() {
                this.addDiscountForm = true;
            },
            async addDiscountToList() {
                let newDiscount = await this.$refs.discountForm.getDiscount();
                if (newDiscount) {
                    newDiscount.modified = true;
                    newDiscount.sliderKey = this.generateSliderKey();
                    this.discounts.push(newDiscount);
                    this.addDiscountForm = false;
                }
                this.showSliders();
            },
            async saveEdit(index) {
                let editRef = this.$refs.discountEditForm.find(
                    el => el.$attrs['data-key'] === index
                );
                let newDiscount = await editRef.getDiscount();
                newDiscount.modified = true;
                this.$set(this.discounts, index, newDiscount);
                this.showSliders();
            },
            async cancelEdit(index) {
                this.discounts[index].editMode = false;
                this.showSliders();
            },
            async deleteDiscount(index = 0, fromList = false) {
                if (fromList) {
                    if (this.discounts.length != 0) {
                        this.discounts.splice(index, 1);
                    }
                }
                this.discountToBeAdded = this.clone(this.discountTemplate);
                if (!fromList) {
                    this.addDiscountForm = false;
                }
                this.showSliders();
            },
            getNewDiscounts() {
                let alteredDiscounts = [];
                for (let discount of this.discounts) {
                    if (discount.modified == true) {
                        alteredDiscounts.push(discount);
                    }
                }
                console.log("altered discounts: ", alteredDiscounts);
                return alteredDiscounts;
            },
            getAllDiscounts() {
                return this.discounts
            },
            async displaySlider(min, max, sliderKey) {
                await this.$nextTick();
                this.slider = $(this.$refs[sliderKey]).slider({
                    min: 1,
                    max: 15,
                    tooltip: 'always',
                    value: [min, max],
                    labelledby: ['slider-left', 'slider-right']
                }).data('slider');
                this.slider.setValue([min, max]);
                this.slider.disable();
            },
            showSliders() {
                for (let i = 0; i < this.discounts.length; i++) {
                    this.displaySlider(this.discounts[i].discountBundleDetails.minRange, this.discounts[i].discountBundleDetails.maxRange, this.discounts[i].sliderKey)
                }
            },
            generateSliderKey() {
                return "slider" + (Math.random() + 1).toString(36).substring(7);
            },
            viewFutureDiscount(discount) {
                let futureSettings = {...discount.futureSettings, effectiveDate: discount.futureSettings.effectiveDate};
                return futureSettings;
            }
        },
        async mounted() {
            let [template] = await Promise.all([
                api.getNoId('Discount', 'EmptyFamilyBundleTemplate')
            ]);
            this.discounts = this.discountsPassed;

            if (this.planId != 0) {
                let data = await api.get('Discount', this.planId, '');
                this.mapData('currentDiscounts', data.current);
                this.futureOnlyDiscounts = data.futureOnly;
                this.hasPatients = data.hasPatients;

                Object.keys(this.currentDiscounts).forEach(key => {
                    let dis = data.current[key];
                    dis.editMode = false;
                    dis.modified = false;
                    dis.exist = true;
                    dis.sliderKey = this.generateSliderKey();
                    let obj = this.discounts.find(o => o.discountId === dis.discountId);
                    if (obj == undefined) {
                        dis.discountBundleDetails = this.parseJSONCamelCase(dis.details);
                        if (Boolean(dis.futureSettings) && dis.futureSettings.discountSettingsId) {
                            dis.futureSettings.discountBundleDetails = this.parseJSONCamelCase(dis.futureSettings.details);
                        }
                        this.discounts.push(dis)
                    }
                })
            }
            this.mapData('discountTemplate', template);
            this.mapData('discountToBeAdded', template);
            this.showSliders();
            this.widgetReady = true;
        },
    }
</script>

<style scoped>
    .btn-sm {
        margin: 0px 4px;
    }

    button[disabled],
    button[disabled]:hover,
    button[disabled]:focus {
        opacity: .5;
    }

    .margin-bottom-3 {
        margin-bottom: 3px;
    }

    .margin-bottom-30 {
        margin-bottom: 30px;
    }

    .word-break-all {
        word-break: break-all;
    }

    .padding-top-5 {
        padding-top: 5px;
    }

    .edit-button {
        background-color: #157347 !important;
        border: 1px solid #157347 !important;
    }

    .edit-button:hover {
        border: 1px solid #157347;
    }

    .edit-button {
        color: white !important;
    }

    .button-container {
        margin-top: 20px;
    }

    .slider-widget /deep/ .slider-selection {
        background: darkgray;
    }

    .slider-widget /deep/ .slider-horizontal {
        margin-left: 8px;
        margin-right: 8px;
    }

    .discount-row {
        min-height: 35px;
    }

    .btn-red {
        border-width: 1px;
    }

    .future-settings-container {
        position: relative;
    }

    .future-discount {
        display: none;
    }

    .pending-link {
        cursor: pointer;
    }

    .pending-link:hover .future-discount {
        display: initial;
    }

</style>